import React, { ForwardedRef, ReactNode } from 'react';
import { AriaModalOverlayProps } from '@react-aria/overlays';

import { SapphireStyleProps, useThemeCheck } from '../../utils';
import {
  ButtonGroup,
  Dialog,
  DialogProps,
  DialogTitle,
  FeedbackMessage,
  FeedbackMessageProps,
  Typography,
} from '../..';
import { GlobalDomAttributes } from '../../utils/types';

export type AlertDialogProps = AriaModalOverlayProps &
  GlobalDomAttributes &
  SapphireStyleProps &
  Pick<DialogProps, 'isOpen' | 'onClose'> &
  Omit<FeedbackMessageProps, 'heading' | 'actions'> & {
    /**
     *
     * @default "horizontal"
     */
    actionsOrientation?: 'vertical' | 'horizontal';
    /**
     * The elements that will be rendered in the footer of the dialog.
     */
    actions: ReactNode;
    /**
     * The title of the dialog.
     */
    heading: string;
  };

/**
 * `AlertDialog` is a wrapper component around `Dialog` and allows you to interrupt the user's
 * experience to prompt them for important confirmations. (e.g. deleting something, confirming
 * an important or irreversible choice etc.).
 * Don't use them for less relevant of unnecessary confirmations.
 *
 * The title should be similar to the action that prompted the user to open it.
 */
export const AlertDialog = React.forwardRef(function AlertDialog(
  props: AlertDialogProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const {
    variant = 'neutral',
    icon,
    body,
    actionsOrientation = 'horizontal',
    actions,
    heading,
    ...rest
  } = props;

  return (
    <Dialog ref={ref} role="alertdialog" size="xs" {...rest} withPadding>
      <FeedbackMessage
        width="100%"
        heading={
          <DialogTitle>
            {(titleProps) => (
              <Typography.Body size="lg" isSemibold {...titleProps}>
                {heading}
              </Typography.Body>
            )}
          </DialogTitle>
        }
        variant={variant}
        icon={icon}
        body={body}
        actions={
          <ButtonGroup
            width="100%"
            stretch="autoVertical"
            orientation={actionsOrientation}
          >
            {actions}
          </ButtonGroup>
        }
      />
    </Dialog>
  );
});
